import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import type {SnapshotRecord} from '@PosterWhiteboard/collaboration/poster-op.types';
import type {FlushOpsResponse} from '@PosterWhiteboard/collaboration/poster-live-edits.types';
import {PosterCollaborationConnection} from '@PosterWhiteboard/collaboration/poster-collaboration-connection.class';
import {PosterPresence} from '@PosterWhiteboard/collaboration/poster-presence.class';
import {PosterLiveEdits} from '@PosterWhiteboard/collaboration/poster-live-edits.class';
import {PosterModeType} from '@PosterWhiteboard/poster/poster-mode.class';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import {GROWL_TYPE} from '@Libraries/message-growl.types';
import {getUniqueString} from '@Utils/string.util';

const FORCED_AUTOSAVE_GROWL_INTERVAL_MS = 6_000;
/** Commits that land while a closing flush waits are flushed too, this many times at most. */
const CLOSE_FLUSH_ATTEMPTS = 3;

/**
 * The tab's op stream and, on top of it, several people editing one poster at once. The op
 * stream (live edits) runs for every editing session the server accepts op envelopes from, and
 * is how the poster is saved. Collaboration adds the realtime connection and the two sides that
 * use it: presence (who is here, their cursors and selections) and the live delivery of the
 * other tabs' envelopes. Started from Poster.onPosterInit and again after a first save gives the
 * poster its id; a tab that is not eligible never connects.
 *
 * The op stream saves as the edits are made, so it is autosave: a tab whose autosave is off (the
 * user's preference, or any of the other reasons SavePoster.isAutoSaveEnabled lists) keeps
 * presence but saves the poster whole, on the Save button only. Shared editing has no such mode:
 * an edit the others see is already in the shared log, and holding edits back until a later Save
 * would land them, stale, over whatever the others did in between. So the moment someone else is
 * in the poster, the stream opens for everyone in it and the tab says so; it stays open until the
 * preference is toggled or the editor is left.
 */
export class PosterCollaboration {
  /** Identifies this tab across presence and edits; one person in two tabs is two sessions. */
  public readonly sessionId = getUniqueString();
  public readonly presence: PosterPresence;
  public readonly liveEdits: PosterLiveEdits;
  #connection: PosterCollaborationConnection | null = null;
  #posterHashId: string | null = null;
  #loadedSeq = 0;
  /** The poster as the stored JSON plus `#loadedSeq` describe it: the point the stream starts from if it opens later in the session. */
  #baseSnapshot: SnapshotRecord | null = null;
  #isOpStreamSaveEnabled = false;

  constructor(private readonly poster: Poster) {
    this.presence = new PosterPresence(poster, this);
    this.liveEdits = new PosterLiveEdits(poster, this);
  }

  /** The live-edit sequence the loaded JSON reflects (from the load response); the log is replayed from here on start. */
  public setLoadedSeq(seq: number): void {
    this.#loadedSeq = seq;
  }

  /** Whether the server lets edits to this design save through the op stream (from the load or first-save response); production streams a share of designs. */
  public setIsOpStreamSaveEnabled(isEnabled: boolean): void {
    this.#isOpStreamSaveEnabled = isEnabled;
  }

  /** Opens the session for a saved poster: the op stream when this tab autosaves, and when collaboration is on, its channels; leaves the previous poster's first. A poster without an id yet (an unsaved copy) saves the old way first. */
  public start(): void {
    this.#start(this.poster.history.getCurrentSnapshot());
  }

  /**
   * The poster was just saved whole for the first time, so its log is empty and the stream starts
   * from `savedSnapshot`; whatever was committed while that save was in flight becomes the first envelope.
   */
  public startAfterFirstSave(savedSnapshot: SnapshotRecord): void {
    this.#loadedSeq = 0;
    this.#start(savedSnapshot);
    this.#sendEditsSince(savedSnapshot);
  }

  /**
   * The live-edit sequence this tab's poster reflects: what it loaded at, then what each whole save
   * stored or the stream folded. A whole save declares it, so the server refuses one that would
   * drop envelopes a fold has stored since.
   */
  public getKnownOpSeq(): number {
    return this.#loadedSeq;
  }

  /** A whole-poster save landed while the stream was closed, so the stored JSON is now `savedSnapshot` at `storedOpSeq`; a stream opened later starts from there. */
  public onPosterSavedWhole(savedSnapshot: SnapshotRecord, storedOpSeq: number | null): void {
    if (this.liveEdits.isActive()) {
      return;
    }
    if (storedOpSeq !== null) {
      this.#loadedSeq = storedOpSeq;
    }
    if (this.#posterHashId !== null) {
      this.#baseSnapshot = savedSnapshot;
    }
  }

  /** The presence roster changed: with someone else in the poster, a tab that was saving whole starts streaming. */
  public onRemoteSessionsChanged(): void {
    if (this.#posterHashId === null || this.liveEdits.isActive() || !this.isSharedWithOthers()) {
      return;
    }
    this.#openEditStream();
    this.#announceAutosaveWhileShared();
  }

  /**
   * The user toggled autosave in the editor. On: the stream opens and carries what was edited since
   * the last whole save. Off, while alone: what the stream still holds goes out, then it closes and
   * the Save button saves the poster whole again. Off, while shared: the stream stays, as described above.
   */
  public async onAutosavePreferenceChanged(): Promise<void> {
    if (this.#posterHashId === null) {
      return;
    }
    const shouldStream = this.#shouldStreamEdits();
    if (shouldStream && !this.liveEdits.isActive()) {
      this.#openEditStream();
      return;
    }
    if (!shouldStream && this.liveEdits.isActive()) {
      const closed = await this.#closeEditStream();
      if (!closed) {
        this.#announceAutosaveStillOn();
      }
    }
  }

  #start(confirmedSnapshot: SnapshotRecord): void {
    if (!this.#isSessionEligible()) {
      this.stop();
      return;
    }
    if (this.#posterHashId === this.poster.hashedID) {
      return;
    }
    this.stop();
    this.#posterHashId = this.poster.hashedID;
    this.#baseSnapshot = confirmedSnapshot;
    if (this.#shouldStreamEdits()) {
      this.liveEdits.open(this.#loadedSeq, confirmedSnapshot);
    }
    if (this.#isCollaborationEligible()) {
      void this.#join(this.#posterHashId).catch(this.#onJoinFailed.bind(this));
    } else if (this.liveEdits.isActive()) {
      void this.liveEdits.replayLog();
    }
  }

  /** Leaves both channels and closes the connection once presence has said goodbye (closing first would discard that message). */
  public stop(): void {
    const connection = this.#connection;
    this.#connection = null;
    this.#posterHashId = null;
    this.#baseSnapshot = null;
    this.liveEdits.leave();
    void this.presence.leave().finally((): void => {
      connection?.close();
    });
  }

  /** Someone else is in the poster right now, so edits should travel as soon as they are made. */
  public isSharedWithOthers(): boolean {
    return this.#connection !== null && this.poster.redux.getRemoteSessionCount() > 0;
  }

  /** Whether the server accepts a session for this poster from this tab at all: presence, and the op stream when it saves. */
  #isSessionEligible(): boolean {
    const {details} = this.poster.mode;
    const isViewingOlderVersion = details.type === PosterModeType.EDIT && Boolean(details.posterVersionId);
    return (
      this.#isOpStreamSaveEnabled &&
      !this.poster.savePoster.areSavesPaused() &&
      window.PMW.getUserId() !== 'notloggedin' &&
      this.poster.id !== '' &&
      this.poster.hashedID !== '' &&
      !isViewingOlderVersion &&
      !this.poster.mode.isGeneration() &&
      !this.poster.mode.isWebpage() &&
      !this.poster.mode.isRegen()
    );
  }

  #shouldStreamEdits(): boolean {
    return this.poster.savePoster.isAutoSaveEnabled() || this.isSharedWithOthers();
  }

  #isCollaborationEligible(): boolean {
    return window.PMW.isPosterCollaborationEnabled && window.PMW.streamTransport !== 'off';
  }

  /** Opens the stream mid-session from the last stored state, with everything edited since as its first envelope, and joins the edits channel when the tab is connected. */
  #openEditStream(): void {
    if (this.#baseSnapshot === null) {
      return;
    }
    const baseSnapshot = this.#baseSnapshot;
    this.liveEdits.open(this.#loadedSeq, baseSnapshot);
    this.#sendEditsSince(baseSnapshot);
    if (this.#connection !== null) {
      void this.liveEdits.join(this.#connection).catch(this.#onJoinFailed.bind(this));
    } else {
      void this.liveEdits.replayLog();
    }
  }

  /**
   * Sends what the stream still holds, has it folded, then closes it: the poster is saved whole
   * from here on, so the next save cannot be a delta, and a stream opened later starts after the
   * fold rather than re-applying this tab's own envelopes. A flush the server did not complete
   * leaves the stream open; closing over unfolded envelopes would let a later fold replay them
   * over a whole save. Resolves with whether the stream closed; it stays open when the tab should
   * still stream (someone arrived meanwhile), which is not a failure.
   */
  async #closeEditStream(): Promise<boolean> {
    const folded = await this.#flushUntilQuiet();
    if (!this.liveEdits.isActive() || this.#shouldStreamEdits()) {
      return true;
    }
    if (folded === null) {
      return false;
    }
    this.#loadedSeq = folded.opSeq;
    this.#baseSnapshot = this.poster.history.getCurrentSnapshot();
    this.liveEdits.leave();
    this.poster.savePoster.onOpStreamClosed(folded.jsonVersionId);
    return true;
  }

  /**
   * Flushes again for commits made during the wait: one still unacknowledged, or one the server
   * ordered after the fold it just reported, so the fold sits behind what this tab has applied.
   * Null when the server did not acknowledge or fold everything within the attempts.
   */
  async #flushUntilQuiet(attemptsLeft = CLOSE_FLUSH_ATTEMPTS): Promise<FlushOpsResponse | null> {
    try {
      const folded = await this.liveEdits.flush();
      if (this.#hasFoldedEverything(folded)) {
        return folded;
      }
      return attemptsLeft > 1 ? await this.#flushUntilQuiet(attemptsLeft - 1) : null;
    } catch (error) {
      window.PMW.log('poster-collaboration: the stream could not be flushed before closing', error);
      return null;
    }
  }

  #hasFoldedEverything(folded: FlushOpsResponse): boolean {
    return !this.liveEdits.hasUnacknowledgedEdits() && folded.opSeq >= this.liveEdits.getLastAppliedSeq();
  }

  /** The preference is off, but the stream could not be closed cleanly, so the tab still saves as it edits. */
  #announceAutosaveStillOn(): void {
    getPosterUi().showMessageGrowl({
      text: window.i18next.t('pmwjs_autosave_could_not_turn_off'),
      type: GROWL_TYPE.DANGER,
      showCloseIcon: true,
    });
  }

  #announceAutosaveWhileShared(): void {
    getPosterUi().showMessageGrowl({
      text: window.i18next.t('pmwjs_autosave_on_while_others_editing'),
      type: GROWL_TYPE.NEUTRAL,
      interval: FORCED_AUTOSAVE_GROWL_INTERVAL_MS,
      showCloseIcon: true,
    });
  }

  #sendEditsSince(snapshot: SnapshotRecord): void {
    this.liveEdits.onLocalCommit(snapshot, this.poster.history.getCurrentSnapshot());
  }

  async #join(posterHashId: string): Promise<void> {
    const connection = new PosterCollaborationConnection(posterHashId);
    const access = await connection.open();
    if (this.#posterHashId !== posterHashId) {
      connection.close();
      return;
    }
    this.#connection = connection;
    this.poster.redux.localParticipantJoined(access.participant);
    this.presence.join(connection);
    if (this.liveEdits.isActive()) {
      await this.liveEdits.join(connection);
    }
  }

  /** Access to the channels was refused or lost; the tab keeps saving through its op stream, edits alone, and still shows what the log holds. */
  #onJoinFailed(error: unknown): void {
    window.PMW.log('poster-collaboration: could not join the poster channels', error);
    if (this.liveEdits.isActive()) {
      void this.liveEdits.replayLog();
    }
  }
}
